/**
 * Maps a browser pathname to a top-level module key recognised by the
 * AI engine `GET /starters` endpoint.
 *
 * The endpoint is permissive — any unknown value falls back to its
 * `default` pool — so this helper deliberately returns a plain `string`
 * (or `null` when the path matches none of the known prefixes). We
 * intentionally do NOT mirror the backend's accepted-key enum here:
 * keeping the contract in one place (the backend) avoids drift and
 * lets the BE add new pools without a paired FE type bump.
 *
 * IMPORTANT: the returned values are deliberately flat (e.g. `'risk'`),
 * NOT dot-path `ModuleKey` values from `packages/modules/src/defaults.ts`
 * (e.g. `'risk.subModules.appetite'`). The starters backend has a flat
 * key set; sending a dot-path silently falls back to the `default` pool.
 */

// Ordered: first match wins. More specific prefixes must precede less specific.
const PREFIX_MAP: { prefix: RegExp; key: string }[] = [
  { prefix: /^\/risks(\/|$)/, key: 'risk' },
  { prefix: /^\/appetites(\/|$)/, key: 'risk' },
  { prefix: /^\/acceptances(\/|$)/, key: 'risk' },

  { prefix: /^\/controls(\/|$)/, key: 'control' },
  { prefix: /^\/control-groups(\/|$)/, key: 'control' },

  { prefix: /^\/actions(\/|$)/, key: 'action' },

  { prefix: /^\/issues(\/|$)/, key: 'issue' },
  { prefix: /^\/sar-log(\/|$)/, key: 'issue' },
  { prefix: /^\/gdpr-breach-log(\/|$)/, key: 'issue' },
  { prefix: /^\/pci-breach-log(\/|$)/, key: 'issue' },
  { prefix: /^\/breach-log(\/|$)/, key: 'issue' },
  { prefix: /^\/consumer-duty(\/|$)/, key: 'issue' },
  { prefix: /^\/customer-trust(\/|$)/, key: 'issue' },
  { prefix: /^\/causes(\/|$)/, key: 'issue' },
  { prefix: /^\/consequences(\/|$)/, key: 'issue' },

  // `/assessments/oversight*` deliberately falls through to `assessment` —
  // the backend has no dedicated `oversight` pool, so the assessment pool
  // is the closest-matching set of starters for oversight workflows.
  { prefix: /^\/assessments(\/|$)/, key: 'assessment' },

  { prefix: /^\/compliance(\/|$)/, key: 'obligation' },

  { prefix: /^\/report-an-issue(\/|$)/, key: 'incident_reporting' },

  { prefix: /^\/indicator(\/|$)/, key: 'indicator' },

  { prefix: /^\/policy(\/|$)/, key: 'document' },
];

export const inferModuleFromPathname = (
  pathname: string | undefined | null
): string | null => {
  if (!pathname) {
    return null;
  }
  for (const { prefix, key } of PREFIX_MAP) {
    if (prefix.test(pathname)) {
      return key;
    }
  }

  return null;
};
